import React, { useMemo, useState } from 'react'
import {
    BarChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Bar,
    ResponsiveContainer,
    LabelList,
    ComposedChart,
    Legend,
} from 'recharts'
import * as moment from 'moment'
import {
    getAnalyticsColor, darkenColor, formatYAxisLabelWithWrap, calculateYAxisWidth, chartLabelStyle,
} from 'app/analytics'
import { ChartPaginationControls } from 'components'
import { AnalyticsChartWrapper } from './AnalyticsChartWrapper'

// Enhanced date formatting types and utilities
type DateFormatType = 'daily' | 'monthly' | 'yearly' | 'custom'

interface DateFormatConfig {
    type: DateFormatType
    format?: string // moment.js format string for custom formatting
}

// Smart date granularity detection
const detectDateGranularity = (data: any[], labelKey: string): DateFormatType => {
    if (!data || data.length === 0 || !labelKey) return 'daily'

    const dates = data
        .map((item) => item[labelKey])
        .filter((dateStr) => dateStr)
        .map((dateStr) => new Date(dateStr))
        .filter((date) => !isNaN(date.getTime()))

    if (dates.length === 0) return 'daily'

    // Check if all dates are the first day of the month (monthly data)
    const allFirstOfMonth = dates.every((date) => date.getDate() === 1)
    if (allFirstOfMonth) return 'monthly'

    // Check if all dates are January 1st (yearly data)
    const allFirstOfYear = dates.every((date) => date.getDate() === 1 && date.getMonth() === 0)
    if (allFirstOfYear) return 'yearly'

    // Default to daily
    return 'daily'
}

// Enhanced date formatter with multiple format options
const createDateFormatter = (config: DateFormatConfig) => (item: string | number | Date): string => {
    const date = item instanceof Date ? item : new Date(item)

    if (isNaN(date.getTime())) {
        return String(item) // Return original if not a valid date
    }

    switch (config.type) {
        case 'monthly':
            return moment(date).format('MMM YYYY')
        case 'yearly':
            return moment(date).format('YYYY')
        case 'custom':
            return moment(date).format(config.format || 'MMM DD')
        case 'daily':
        default:
            return moment(date).format('MMM DD')
    }
}

// Duration formatting utility
const formatDuration = (milliseconds: number): string => {
    if (!milliseconds || milliseconds < 0) return '0s'

    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
        const remainingHours = hours % 24
        return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
    } if (hours > 0) {
        const remainingMinutes = minutes % 60
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
    } if (minutes > 0) {
        const remainingSeconds = seconds % 60
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
    }
    return `${seconds}s`
}

// Duration field detection
const isDurationField = (fieldName: string): boolean => {
    if (!fieldName) return false
    const lowerName = fieldName.toLowerCase()
    return lowerName.includes('duration') || lowerName.includes('time') || lowerName.includes('elapsed')
}

interface ChartDataItem {
    [key: string]: string | number | undefined
}

interface AnalyticsBarChartProps {
    title?: string
    data: ChartDataItem[]
    labelKey?: string
    dataKey: string
    color?: string
    transitioning?: boolean
    compact?: boolean
    orientation?: 'vertical' | 'horizontal'
    isDuration?: boolean
    durationScale?: { scale: number, unit: string, unitLabel: string }
    hasToggles?: boolean
    // Pagination props
    pageSize?: number
    showPagination?: boolean
    // New date formatting props
    dateFormat?: DateFormatType | string
    autoDetectDateFormat?: boolean
    // New props for multi-series support
    series?: Array<{
        dataKey: string,
        name: string,
        color?: string,
        isDuration?: boolean,
        durationScale?: { scale: number, unit: string, unitLabel: string }
    }>
    // Margin modifier prop
    marginModifier?: number
    onPointClick?: (payload: {
        chartType: string
        labelKey: string
        labelValue: string | number
        row: any
    }) => void
}

interface CustomLabelProps {
    x: number
    y: number
    width: number
    height: number
    value: string | number
    fill: string
    isDuration?: boolean
    orientation?: 'vertical' | 'horizontal'
}

function CustomLabel(props: CustomLabelProps) {
    const {
        x,
        y,
        width,
        height,
        value,
        fill,
        isDuration = false,
        orientation = 'vertical',
    } = props

    // Validate coordinates to prevent NaN warnings
    if (x === undefined || y === undefined || width === undefined || height === undefined
        || Number.isNaN(Number(x)) || Number.isNaN(Number(y))
        || Number.isNaN(Number(width)) || Number.isNaN(Number(height))) {
        return null
    }

    // Use a darkened version of the bar color for better readability
    const labelColor = darkenColor(fill || '#2c3e50', 25)

    // Format the value if it's a duration
    let displayValue = value
    if (isDuration && typeof value === 'number') {
        displayValue = formatDuration(value)
    }

    if (orientation === 'horizontal') {
        // Position label at the end of the horizontal bar (to the right)
        return (
            <text
                x={Number(x) + Number(width) + 8}
                y={Number(y) + Number(height) / 2 + 4}
                style={chartLabelStyle}
                fill={labelColor}
                textAnchor="start"
            >
                {displayValue}
            </text>
        )
    }
    // Position label above the vertical bar
    const xPos = Number(x) + Number(width) / 2
    const yPos = Number(y) - 8

    return (
        <text
            x={xPos}
            y={yPos}
            style={chartLabelStyle}
            fill={labelColor}
            textAnchor="middle"
        >
            {displayValue}
        </text>
    )
}

export function AnalyticsBarChart(props: AnalyticsBarChartProps) {
    const {
        title,
        data,
        labelKey,
        dataKey,
        color,
        transitioning,
        isDuration = false,
        durationScale,
        hasToggles = false,
        pageSize = 10,
        showPagination = false,
        dateFormat,
        autoDetectDateFormat = true,
        series = [],
        onPointClick,
    } = props
    const marginModifier = props.marginModifier || 1.0
    const densityClass = marginModifier <= 0.4
        ? 'density-quarter'
        : marginModifier <= 0.75
            ? 'density-half'
            : 'density-full'
    const isQuarterDensity = marginModifier <= 0.4
    const isHalfDensity = marginModifier > 0.4 && marginModifier <= 0.75

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1)

    // Detect if labelKey contains 'date' for date-based processing
    const isDateField = labelKey ? labelKey.toLowerCase().includes('date') : false

    // Enhanced date formatting configuration
    const dateFormatConfig = useMemo((): DateFormatConfig => {
        if (!isDateField) {
            return { type: 'daily' }
        }

        // If dateFormat is explicitly provided
        if (dateFormat) {
            if (typeof dateFormat === 'string') {
                // Check if it's a predefined type
                if (['daily', 'monthly', 'yearly'].includes(dateFormat)) {
                    return { type: dateFormat as DateFormatType }
                }
                // Treat as custom moment.js format string
                return { type: 'custom', format: dateFormat }
            }
        }

        // Auto-detect if enabled and no explicit format provided
        if (autoDetectDateFormat && data && data.length > 0 && labelKey) {
            const detectedType = detectDateGranularity(data, labelKey)
            return { type: detectedType }
        }

        // Default to daily
        return { type: 'daily' }
    }, [isDateField, dateFormat, autoDetectDateFormat, data, labelKey])

    // Create the appropriate date formatter
    const dateFormatter = useMemo(() => createDateFormatter(dateFormatConfig), [dateFormatConfig])

    // Detect if dataKey is a rate field for percentage formatting
    const isRateField = dataKey && (dataKey.toLowerCase().includes('rate') || dataKey.includes('Rate'))

    // Custom Y-axis formatter for duration values
    const yAxisFormatter = (value: number) => {
        if (isDuration && durationScale) {
            // For Y-axis, show the scaled numeric value with unit indicator
            return `${value}${durationScale.unit}`
        }
        return formatYAxisLabelWithWrap(value, 20, dataKey)
    }

    // Custom tooltip formatter that shows the full formatted duration
    const customTooltipFormatter = (value: any, name: string, props: any) => {
        if (isDuration && props.payload && props.payload[`${name}_formatted`]) {
            return [props.payload[`${name}_formatted`], name]
        }
        return [value, name]
    }

    // Sort data chronologically if it's date data
    const processedData = useMemo(() => {
        if (!data || data.length === 0) return []

        if (isDateField && labelKey) {
            // Create a copy of data and sort by date
            return [...data].sort((a, b) => {
                const dateA = a[labelKey] ? new Date(a[labelKey] as string).getTime() : 0
                const dateB = b[labelKey] ? new Date(b[labelKey] as string).getTime() : 0
                return dateA - dateB
            })
        }

        return data
    }, [data, labelKey, isDateField])

    // Calculate pagination
    const totalItems = processedData.length
    const totalPages = Math.ceil(totalItems / pageSize)
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = Math.min(startIndex + pageSize, totalItems)

    // Get paginated data
    const paginatedData = useMemo(() => {
        if (!showPagination || pageSize <= 0) {
            return processedData
        }
        return processedData.slice(startIndex, endIndex)
    }, [processedData, currentPage, pageSize, showPagination, startIndex, endIndex])

    // Pagination navigation functions
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1)
        }
    }

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
        }
    }

    const goToPage = (pageNumber: number) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber)
        }
    }

    // Reset to first page when data changes
    useMemo(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1)
        }
    }, [totalPages, currentPage])

    // Calculate optimal left margin based on Y-axis labels (for numeric values) - use paginatedData
    const leftMargin = useMemo(() => {
        if (!paginatedData || paginatedData.length === 0 || !dataKey) return 60

        // Find the maximum value to determine label width
        const maxValue = Math.max(...paginatedData.map((item) => Number(item[dataKey]) || 0))
        const formattedMaxLabel = isDuration && durationScale
            ? `${maxValue}${durationScale.unit}`
            : formatYAxisLabelWithWrap(maxValue)

        // Calculate margin based on label length (roughly 8px per character + 30px padding)
        const baseMargin = Math.max(formattedMaxLabel.length * 8 + 40, 70)

        // Apply margin modifier (default to 1.0 if not provided)
        const modifier = marginModifier
        return Math.round(baseMargin * modifier)
    }, [paginatedData, dataKey, isDuration, durationScale, marginModifier])

    // Calculate optimal bottom margin based on X-axis label length - use paginatedData
    const bottomMargin = useMemo(() => {
        if (!paginatedData || paginatedData.length === 0 || !labelKey) return 60

        // Find the longest label after formatting
        const maxLabelLength = Math.max(...paginatedData.map((item) => {
            const label = item[labelKey]
            if (isDateField && label) {
                // Use the formatted date length
                const formattedLabel = dateFormatter(label)
                return formattedLabel.length
            } if (typeof label === 'string') {
                return label.length
            }
            return String(label || '').length
        }))

        // If we have many items or long labels, we'll need more space for rotation
        const needsRotation = paginatedData.length > 8 || maxLabelLength > 15

        let baseMargin: number
        if (needsRotation) {
            // For rotated labels, calculate based on label length at 45-degree angle
            // Approximate height = label_length * character_width * sin(45°)
            const rotatedHeight = maxLabelLength * 2 * 0.707 // sin(45°) ≈ 0.707
            baseMargin = Math.max(rotatedHeight + 20, 40) // 40px padding
        } else {
            // For horizontal labels, standard height based on font size
            baseMargin = 30 + Math.max(maxLabelLength > 10 ? 20 : 10, 10)
        }

        // Apply margin modifier (default to 1.0 if not provided)
        let modifier = marginModifier
        // If paginated, don't modify margin
        if (showPagination) modifier = 1.0
        const computedMargin = Math.round(baseMargin * modifier)

        // Keep compact dashboard tiles tighter vertically.
        if (isQuarterDensity) {
            return Math.max(12, Math.min(computedMargin, 24))
        }
        if (isHalfDensity) {
            return Math.max(20, Math.min(computedMargin, 42))
        }

        return computedMargin
    }, [paginatedData, labelKey, isDateField, dateFormatter, marginModifier, showPagination, isQuarterDensity, isHalfDensity])

    // Calculate top margin based on toggle presence
    const topMargin = useMemo(() => {
        if (isQuarterDensity) {
            return hasToggles ? 34 : 14
        }
        if (isHalfDensity) {
            return hasToggles ? 46 : 20
        }

        const baseMargin = hasToggles ? 86 : 42
        return Math.round(baseMargin * marginModifier)
    }, [hasToggles, marginModifier, isQuarterDensity, isHalfDensity])

    // Function to detect if series have vastly different scales - use paginatedData
    const analyzeSeriesScales = useMemo(() => {
        if (series.length < 2) return { needsDualAxis: false, leftSeries: series, rightSeries: [] }

        // Calculate max values for each series
        const seriesStats = series.map((s) => {
            const values = paginatedData.map((item) => Number(item[s.dataKey]) || 0).filter((v) => v > 0)
            const maxValue = Math.max(...values)
            const avgValue = values.reduce((sum, v) => sum + v, 0) / values.length

            const result = {
                ...s,
                maxValue,
                avgValue,
                isPercentage: s.dataKey.toLowerCase().includes('rate') || s.name.toLowerCase().includes('rate') || s.name.includes('%'),
                isDuration: s.isDuration || isDurationField(s.dataKey),
            }

            return result
        })

        // Group series by scale characteristics
        const percentageSeries = seriesStats.filter((s) => s.isPercentage || s.maxValue <= 1)
        const durationSeries = seriesStats.filter((s) => s.isDuration && !s.isPercentage && s.maxValue > 1)
        const countSeries = seriesStats.filter((s) => !s.isPercentage && !s.isDuration && s.maxValue > 1)

        // If we have percentage/rate data mixed with count data, use dual axis
        const hasPercentages = percentageSeries.length > 0
        const hasCounts = countSeries.length > 0 || durationSeries.length > 0

        if (hasPercentages && hasCounts) {
            // Put percentages on right axis, everything else on left
            const result = {
                needsDualAxis: true,
                leftSeries: [...countSeries, ...durationSeries],
                rightSeries: percentageSeries,
            }
            return result
        }

        // Check for order of magnitude differences between count series
        if (countSeries.length >= 2) {
            const maxOfMaxes = Math.max(...countSeries.map((s) => s.maxValue))
            const minOfMaxes = Math.min(...countSeries.map((s) => s.maxValue))

            // If there's more than 10x difference, split into two axes
            // (e.g. Visits ~1800 vs Meetings ~32 is ~56x — should use dual axis)
            if (maxOfMaxes / minOfMaxes > 10) {
                // Sort by maxValue descending to find the natural break point
                const sortedBySize = [...countSeries].sort((a, b) => b.maxValue - a.maxValue)

                // Find where the big gap is: largest series that are 5x bigger than the rest go on left
                let splitIndex = 1
                for (let i = 1; i < sortedBySize.length; i++) {
                    if (sortedBySize[0].maxValue / sortedBySize[i].maxValue > 5) {
                        splitIndex = i
                        break
                    }
                }

                // Identify which series keys go on left vs right
                const leftKeys = new Set(sortedBySize.slice(0, splitIndex).map((s) => s.dataKey))

                // Preserve the original series order for rendering
                const leftSeriesOrdered = seriesStats.filter((s) => leftKeys.has(s.dataKey))
                const rightSeriesOrdered = seriesStats.filter((s) => !leftKeys.has(s.dataKey) && !s.isDuration && !s.isPercentage)
                return {
                    needsDualAxis: true,
                    leftSeries: [...leftSeriesOrdered, ...durationSeries],
                    rightSeries: rightSeriesOrdered,
                }
            }
        }

        return { needsDualAxis: false, leftSeries: seriesStats, rightSeries: [] }
    }, [series, paginatedData])

    // Custom formatters for different axis types
    const leftAxisFormatter = (value: number) => {
        const leftSeriesItem = analyzeSeriesScales.leftSeries[0]
        if (leftSeriesItem?.isDuration && leftSeriesItem?.durationScale) {
            return `${value}${leftSeriesItem.durationScale.unit}`
        }
        return formatYAxisLabelWithWrap(value, 20, leftSeriesItem?.dataKey)
    }

    const rightAxisFormatter = (value: number) => {
        const rightSeriesItem = analyzeSeriesScales.rightSeries[0]
        if (rightSeriesItem?.isPercentage) {
            return `${value}%`
        }
        if (rightSeriesItem?.isDuration && rightSeriesItem?.durationScale) {
            return `${value}${rightSeriesItem.durationScale.unit}`
        }
        return formatYAxisLabelWithWrap(value, 20, rightSeriesItem?.dataKey)
    }

    if (!processedData || processedData.length === 0) {
        return (
            <div className={`analytics-bar-chart${onPointClick ? ' drillable' : ''}`}>
                <h3>{title}</h3>
                <span>No Data to Display</span>
            </div>
        )
    }

    // Use deterministic color based on chart title/dataKey combination
    const barColor = color || getAnalyticsColor(`${title || ''}-${dataKey}`)

    // Create a custom label component that includes dataKey context
    const CustomLabelWithDataKey = (labelProps: any) => {
        let formattedValue = labelProps.value

        if (isDuration && labelProps.payload && labelProps.payload[`${dataKey}_formatted`]) {
            // For duration fields, use the pre-formatted duration string
            formattedValue = labelProps.payload[`${dataKey}_formatted`]
        } else if (isDuration && typeof labelProps.value === 'number' && durationScale) {
            // Fallback: if we don't have the formatted value, format the original value
            // Convert scaled value back to original milliseconds for formatting
            const originalValue = labelProps.value * durationScale.scale
            formattedValue = formatDuration(originalValue)
        } else if (isRateField) {
            formattedValue = `${labelProps.value}%`
        } else if (typeof labelProps.value === 'number' && (dataKey.toLowerCase().includes('cost') || dataKey.toLowerCase().includes('cac') || dataKey.toLowerCase().includes('fdc') || dataKey.toLowerCase().includes('spend'))) {
            // Format currency values for cost/spend fields
            if (labelProps.value < 1) {
                formattedValue = `$${labelProps.value.toFixed(3).replace(/\.?0+$/, '')}`
            } else if (labelProps.value >= 100) {
                formattedValue = `$${Math.round(labelProps.value).toLocaleString()}`
            } else {
                formattedValue = `$${labelProps.value.toFixed(2)}`
            }
        }

        return <CustomLabel {...labelProps} value={formattedValue} fill={barColor} isDuration={isDuration} orientation={props.orientation} />
    }

    // Pagination controls component
    const PaginationControls = () => (
        <ChartPaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            showPagination={showPagination}
            onPrevPage={goToPrevPage}
            onNextPage={goToNextPage}
            onGoToPage={goToPage}
        />
    )

    // Keep dual-axis composed charts visually balanced and reserve room for legend.
    const composedSideMargin = Math.max(leftMargin, isQuarterDensity ? 22 : isHalfDensity ? 30 : 48)
    const composedAxisWidth = Math.max(isQuarterDensity ? 22 : isHalfDensity ? 30 : 40, composedSideMargin - 10)
    const composedLegendTop = isQuarterDensity ? 52 : isHalfDensity ? 58 : 54
    const composedTopMargin = topMargin + (isQuarterDensity ? 42 : isHalfDensity ? 40 : 46)
    const noLegendTabsTopBoost = hasToggles ? (isQuarterDensity ? 20 : isHalfDensity ? 10 : 8) : 0
    const legendWrapperStyle = {
        paddingTop: '0px',
        left: 0,
        right: 0,
        width: '100%',
        textAlign: 'center' as const,
        top: `${composedLegendTop}px`,
    }

    const emitPointClick = (entry: any) => {
        if (!onPointClick || !labelKey) {
            return
        }
        const row = entry?.payload || entry
        const labelValue = row?.[labelKey]
        if (labelValue === undefined || labelValue === null) {
            return
        }
        onPointClick({
            chartType: 'bar',
            labelKey,
            labelValue,
            row,
        })
    }

    return (
        <div className={`analytics-bar-chart ${densityClass}${onPointClick ? ' drillable' : ''}`} style={{ position: 'relative' }}>
            <h3>{title}</h3>
            <div className="analytics-chart-view">
                <AnalyticsChartWrapper>
                    <ResponsiveContainer width="100%" height="100%">
                        {(analyzeSeriesScales.needsDualAxis || series.length > 1) ? (
                            <ComposedChart
                                data={paginatedData}
                                barGap={0}
                                margin={{
                                    top: composedTopMargin,
                                    right: composedSideMargin,
                                    bottom: bottomMargin,
                                    left: composedSideMargin,
                                }}
                            >
                                <CartesianGrid strokeDashArray="3 3" stroke="#e0e6ed" />
                                <XAxis
                                    dataKey={labelKey}
                                    tickFormatter={isDateField ? dateFormatter : undefined}
                                    type={isDateField ? 'category' : undefined}
                                    allowDataOverflow={isDateField}
                                    interval={0}
                                    angle={paginatedData.length > 8 || bottomMargin > 80 ? -45 : 0}
                                    textAnchor={paginatedData.length > 8 || bottomMargin > 80 ? 'end' : 'middle'}
                                    height={bottomMargin}
                                />
                                {/* Left Y-axis for primary metrics (counts, durations) */}
                                <YAxis
                                    yAxisId="left"
                                    tickFormatter={leftAxisFormatter}
                                    width={composedAxisWidth}
                                    interval={0}
                                />
                                {/* Right Y-axis for secondary metrics (percentages) */}
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tickFormatter={rightAxisFormatter}
                                    width={composedAxisWidth}
                                    interval={0}
                                />
                                {/* Render left axis series */}
                                {analyzeSeriesScales.leftSeries.map((s, index) => (
                                    <Bar
                                        key={`bar-left-${s.dataKey}`}
                                        yAxisId="left"
                                        dataKey={s.dataKey}
                                        fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index)}
                                        name={s.name}
                                        onClick={emitPointClick}
                                    >
                                        {!transitioning && (
                                            <LabelList
                                                dataKey={s.dataKey}
                                                content={(labelProps: any) => {
                                                    let formattedValue = labelProps.value
                                                    if (s.isDuration && labelProps.payload && labelProps.payload[`${s.dataKey}_formatted`]) {
                                                        formattedValue = labelProps.payload[`${s.dataKey}_formatted`]
                                                    } else if (s.isDuration && typeof labelProps.value === 'number' && s.durationScale) {
                                                        const originalValue = labelProps.value * s.durationScale.scale
                                                        formattedValue = formatDuration(originalValue)
                                                    } else if (s.name && s.name.includes('$') && typeof labelProps.value === 'number') {
                                                        // Format as currency if series name contains $
                                                        if (labelProps.value < 1) {
                                                            formattedValue = `$${labelProps.value.toFixed(3).replace(/\.?0+$/, '')}`
                                                        } else if (labelProps.value >= 100) {
                                                            formattedValue = `$${Math.round(labelProps.value).toLocaleString()}`
                                                        } else {
                                                            formattedValue = `$${labelProps.value.toFixed(2)}`
                                                        }
                                                    }
                                                    return <CustomLabel {...labelProps} value={formattedValue} fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index)} isDuration={s.isDuration} orientation={props.orientation} />
                                                }}
                                            />
                                        )}
                                    </Bar>
                                ))}
                                {/* Render right axis series */}
                                {analyzeSeriesScales.rightSeries.map((s, index) => (
                                    <Bar
                                        key={`bar-right-${s.dataKey}`}
                                        yAxisId="right"
                                        dataKey={s.dataKey}
                                        fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index + analyzeSeriesScales.leftSeries.length)}
                                        name={s.name}
                                        onClick={emitPointClick}
                                    >
                                        {!transitioning && (
                                            <LabelList
                                                dataKey={s.dataKey}
                                                content={(labelProps: any) => {
                                                    let formattedValue = labelProps.value
                                                    if (s.isPercentage) {
                                                        formattedValue = `${labelProps.value}%`
                                                    } else if (s.isDuration && labelProps.payload && labelProps.payload[`${s.dataKey}_formatted`]) {
                                                        formattedValue = labelProps.payload[`${s.dataKey}_formatted`]
                                                    } else if (s.name && s.name.includes('$') && typeof labelProps.value === 'number') {
                                                        // Format as currency if series name contains $
                                                        if (labelProps.value < 1) {
                                                            formattedValue = `$${labelProps.value.toFixed(3).replace(/\.?0+$/, '')}`
                                                        } else if (labelProps.value >= 100) {
                                                            formattedValue = `$${Math.round(labelProps.value).toLocaleString()}`
                                                        } else {
                                                            formattedValue = `$${labelProps.value.toFixed(2)}`
                                                        }
                                                    }
                                                    return <CustomLabel {...labelProps} value={formattedValue} fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index + analyzeSeriesScales.leftSeries.length)} isDuration={s.isDuration} orientation={props.orientation} />
                                                }}
                                            />
                                        )}
                                    </Bar>
                                ))}
                                <Legend
                                    wrapperStyle={legendWrapperStyle}
                                    align="center"
                                    verticalAlign="top"
                                    iconType="circle"
                                    formatter={(value, entry, index) => {
                                        const series = analyzeSeriesScales.leftSeries.concat(analyzeSeriesScales.rightSeries)
                                        const found = series.find((s) => s.name === value)
                                        const color = found ? found.color || getAnalyticsColor(`${title || ''}-${found.dataKey}`, index) : '#000'
                                        return <span style={{ color }}>{value}</span>
                                    }}
                                />
                            </ComposedChart>
                        ) : (
                            <BarChart
                                data={paginatedData}
                                barGap={0}
                                margin={{
                                    top: topMargin + (isQuarterDensity ? 10 : isHalfDensity ? 12 : 16) + noLegendTabsTopBoost,
                                    right: leftMargin,
                                    bottom: bottomMargin,
                                    left: leftMargin,
                                }}
                            >
                                <CartesianGrid strokeDashArray="3 3" stroke="#e0e6ed" />
                                <XAxis
                                    dataKey={labelKey}
                                    tickFormatter={isDateField ? dateFormatter : undefined}
                                    type={isDateField ? 'category' : undefined}
                                    allowDataOverflow={isDateField}
                                    interval={0}
                                    angle={paginatedData.length > 8 || bottomMargin > 80 ? -45 : 0}
                                    textAnchor={paginatedData.length > 8 || bottomMargin > 80 ? 'end' : 'middle'}
                                    height={bottomMargin}
                                />
                                <YAxis
                                    tickFormatter={yAxisFormatter}
                                    width={leftMargin - 20}
                                    interval={0}
                                />
                                {series.length > 0 ? (
                                    series.map((s, index) => (
                                        <Bar
                                            key={`bar-${s.dataKey}`}
                                            dataKey={s.dataKey}
                                            fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index)}
                                            name={s.name}
                                            onClick={emitPointClick}
                                        >
                                            {!transitioning && (
                                                <LabelList
                                                    dataKey={s.dataKey}
                                                    content={(labelProps: any) => {
                                                        let formattedValue = labelProps.value
                                                        const seriesIsDuration = s.isDuration || isDurationField(s.dataKey)
                                                        const seriesIsRate = s.dataKey.toLowerCase().includes('rate') || s.name.toLowerCase().includes('rate') || s.name.includes('%')

                                                        if (seriesIsDuration && labelProps.payload && labelProps.payload[`${s.dataKey}_formatted`]) {
                                                            formattedValue = labelProps.payload[`${s.dataKey}_formatted`]
                                                        } else if (seriesIsDuration && typeof labelProps.value === 'number' && s.durationScale) {
                                                            const originalValue = labelProps.value * s.durationScale.scale
                                                            formattedValue = formatDuration(originalValue)
                                                        } else if (seriesIsRate) {
                                                            formattedValue = `${labelProps.value}%`
                                                        } else if (typeof labelProps.value === 'number' && (s.dataKey.toLowerCase().includes('cost') || s.name.includes('$'))) {
                                                            // Format currency values for cost fields or series with $ in name
                                                            if (labelProps.value < 1) {
                                                                formattedValue = `$${labelProps.value.toFixed(3).replace(/\.?0+$/, '')}`
                                                            } else if (labelProps.value >= 100) {
                                                                formattedValue = `$${Math.round(labelProps.value).toLocaleString()}`
                                                            } else {
                                                                formattedValue = `$${labelProps.value.toFixed(2)}`
                                                            }
                                                        }

                                                        return <CustomLabel {...labelProps} value={formattedValue} fill={s.color || getAnalyticsColor(`${title || ''}-${s.dataKey}`, index)} isDuration={seriesIsDuration} orientation={props.orientation} />
                                                    }}
                                                />
                                            )}
                                        </Bar>
                                    ))
                                ) : (
                                    <Bar
                                        dataKey={dataKey}
                                        fill={barColor}
                                        onClick={emitPointClick}
                                    >
                                        {!transitioning && (
                                            <LabelList
                                                dataKey={dataKey}
                                                content={<CustomLabelWithDataKey />}
                                            />
                                        )}
                                    </Bar>
                                )}
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </AnalyticsChartWrapper>
            </div>
            <PaginationControls />
        </div>
    )
}
